import { useState, useEffect } from 'react';
import { adminPromptAPI } from '../../services/api';

function PromptTemplatePage() {
  const [templates, setTemplates] = useState([]);
  const [selectedType, setSelectedType] = useState('CALENDAR');
  const [content, setContent] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const templateTypes = [
    { value: 'CALENDAR', label: '캘린더 포스트', desc: '캘린더 생성 시 사용되는 프롬프트' },
  ];

  const availableVars = [
    '{Account_Name}', '{Category}', '{Target_Audience}', '{Key_Products}',
    '{Core_Value}', '{Visual_Mood}', '{Voice_&_Tone}', '{Content_Ratio}',
    '{Feed_Count}', '{Brand_Color}', '{Permanent_Tags}',
    '{Calendar_Theme}', '{Post_Index}', '{Subject}', '{Mood}'
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    const t = templates.find(t => t.type === selectedType);
    if (t) {
      setContent(t.content || '');
      setDescription(t.description || '');
    }
  }, [selectedType, templates]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await adminPromptAPI.getAll();
      if (response.success) {
        setTemplates(response.data || []);
      }
    } catch (err) {
      setError('템플릿 로드 실패: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content.trim()) {
      setError('프롬프트 내용을 입력해주세요');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const response = await adminPromptAPI.update(selectedType, { content, description });
      if (response.success) {
        setSuccess('프롬프트 템플릿이 저장되었습니다.');
        setTimeout(() => setSuccess(null), 3000);
        await loadTemplates();
      }
    } catch (err) {
      setError('저장 실패: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (varName) => {
    setContent(prev => prev + varName);
  };

  return (
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 20px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '8px' }}>
          프롬프트 템플릿 관리
        </h2>
        <p style={{ color: '#9d9db9', marginBottom: '32px', fontSize: '14px' }}>
          AI 콘텐츠 생성에 사용되는 프롬프트 템플릿을 관리합니다. 모든 사용자에게 기본 적용됩니다.
        </p>

        {error && (
            <div style={{
              padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: '8px', marginBottom: '16px', color: '#ef4444', fontSize: '14px'
            }}>{error}</div>
        )}
        {success && (
            <div style={{
              padding: '12px 16px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
              borderRadius: '8px', marginBottom: '16px', color: '#22c55e', fontSize: '14px'
            }}>{success}</div>
        )}

        {/* 템플릿 타입 선택 */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
            템플릿 타입
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            {templateTypes.map(t => (
                <button
                    key={t.value}
                    onClick={() => setSelectedType(t.value)}
                    style={{
                      padding: '12px 24px',
                      background: selectedType === t.value ? '#1313ec' : '#1c1c27',
                      color: 'white',
                      border: `1px solid ${selectedType === t.value ? '#1313ec' : '#3b3b54'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                >
                  {t.label}
                </button>
            ))}
          </div>
          <p style={{ fontSize: '12px', color: '#9d9db9', marginTop: '8px' }}>
            {templateTypes.find(t => t.value === selectedType)?.desc}
          </p>
        </div>

        {/* 설명 */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
            설명
          </label>
          <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="템플릿 설명 (관리용)"
              style={{
                width: '100%', padding: '12px 16px', height: '48px',
                background: '#1c1c27', border: '1px solid #3b3b54', borderRadius: '8px',
                outline: 'none', fontSize: '14px'
              }}
          />
        </div>

        {/* 사용 가능 변수 */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
            사용 가능 변수 (클릭하여 삽입)
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {availableVars.map(v => (
                <button
                    key={v}
                    onClick={() => insertVariable(v)}
                    style={{
                      padding: '4px 10px',
                      background: 'rgba(19,19,236,0.1)',
                      color: '#1313ec',
                      border: '1px solid rgba(19,19,236,0.2)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily: 'monospace'
                    }}
                >
                  {v}
                </button>
            ))}
          </div>
        </div>

        {/* 프롬프트 편집기 */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
            프롬프트 내용
          </label>
          {loading ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#9d9db9' }}>로딩 중...</div>
          ) : (
              <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  maxLength={10000}
                  style={{
                    width: '100%', padding: '16px', minHeight: '400px',
                    background: '#1c1c27', border: '1px solid #3b3b54', borderRadius: '12px',
                    outline: 'none', resize: 'vertical', fontSize: '13px',
                    fontFamily: 'monospace', lineHeight: '1.6'
                  }}
              />
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
            <span style={{ fontSize: '12px', color: '#9d9db9' }}>
              {content.length.toLocaleString()} / 10,000 자
            </span>
            {content.length > 8000 && (
                <span style={{ fontSize: '12px', color: '#f59e0b' }}>
                  프롬프트가 길면 토큰 비용이 증가할 수 있습니다
                </span>
            )}
          </div>
        </div>

        {/* 저장 버튼 */}
        <button
            onClick={handleSave}
            disabled={saving || !content.trim()}
            style={{
              padding: '14px 32px',
              background: saving || !content.trim() ? '#282839' : '#1313ec',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: saving || !content.trim() ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '700'
            }}
        >
          {saving ? '저장 중...' : '템플릿 저장'}
        </button>
      </div>
  );
}

export default PromptTemplatePage;
